import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import Loading from "../../common/components/loading/loading";
import {
  appendTimeSeriesTooltipOverlay,
  appendSignedVerticalAreaGradients,
  buildTooltipHtml,
  createZeroAnchoredArea,
  createTooltipHandlers,
  dashboardSeriesColors,
  formatTooltipDate,
  formatTooltipNumber,
  getNumericExtentIncludingZero,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const LineGraph = ({ data, title, isLoading }) => {
  const svgRef = useRef();
  const tooltipRef = useRef(null);

  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);

    if (!svg.node()) {
      return;
    }

    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight;
    const margin = { top: 30, right: 20, bottom: 30, left: 50 };

    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    const parseDate = d3.timeParse("%Y-%m-%dT%H:%M:%SZ");

    const formattedData = data
      .filter((d) => d.forecastTransactionDate && d.mostLikely !== null)
      .map((d) => ({
        forecastTransactionDate: parseDate(d.forecastTransactionDate),
        mostLikely: d.mostLikely,
        actual: d.actual,
      }));

    const normalizedData = formattedData.map((d) => ({
      ...d,
      normalizedDate: d3.timeDay(d.forecastTransactionDate),
    }));

    const dailyAveragesMap = d3.rollups(
      normalizedData,
      (values) => d3.mean(values, (d) => d.mostLikely),
      (d) => +d.normalizedDate,
    );

    const dailyAveragesFilter = new Map(dailyAveragesMap);

    const dailyAverages = formattedData.map((d) => ({
      forecastTransactionDate: d.forecastTransactionDate,
      avgMostLikely: dailyAveragesFilter.get(+d3.timeDay(d.forecastTransactionDate)),
    }));

    const chartSvg = svg
      .append("svg")
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none")
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(formattedData, (d) => d.forecastTransactionDate))
      .range([0, innerWidth]);

    const yScale = d3
      .scaleLinear()
      .domain(
        getNumericExtentIncludingZero([
          ...formattedData.flatMap((d) => [d.mostLikely, d.actual]),
          ...dailyAverages.map((d) => d.avgMostLikely),
        ]),
      )
      .nice()
      .range([innerHeight, 0]);

    chartSvg
      .append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .attr("class", "d3-chart-axis");

    chartSvg
      .append("g")
      .call(d3.axisLeft(yScale))
      .attr("class", "d3-chart-axis");

    chartSvg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -innerHeight / 2)
      .attr("y", -margin.left + 10)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("class", "d3-chart-axis-label")
      .text("MW/h");

    chartSvg
      .append("g")
      .attr("class", "grid d3-chart-gridline")
      .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

    const series = [
      {
        key: "actual",
        label: "Actual",
        color: dashboardSeriesColors.supportGreen,
        data: formattedData,
        valueAccessor: (d) => d.actual,
        strokeWidth: 2.15,
        className: "line dashboard-series-support",
        hasGradient: false,
      },
      {
        key: "avgMostLikely",
        label: "Avg/Day",
        color: dashboardSeriesColors.accentRed,
        data: dailyAverages,
        valueAccessor: (d) => d.avgMostLikely,
        strokeWidth: 2.25,
        className: "line dashboard-series-support",
        hasGradient: false,
      },
      {
        key: "mostLikely",
        label: "Most Likely",
        color: dashboardSeriesColors.accentBlue,
        data: formattedData,
        valueAccessor: (d) => d.mostLikely,
        strokeWidth: 2.5,
        className: "line dashboard-series-primary dashboard-series-dashed",
        strokeDasharray: "4 4",
        hasGradient: true,
      },
    ];

    const areaLayer = chartSvg.append("g").attr("class", "dashboard-area-layer");
    const lineLayer = chartSvg.append("g").attr("class", "dashboard-line-layer");

    series
      .filter((seriesConfig) => seriesConfig.hasGradient)
      .forEach((seriesConfig) => {
        const gradients = appendSignedVerticalAreaGradients({
          chartSvg,
          idPrefix: `hourly-forecast-${seriesConfig.key}`,
          color: seriesConfig.color,
        });

        const positiveArea = createZeroAnchoredArea({
          valueAccessor: seriesConfig.valueAccessor,
          xAccessor: (d) => d.forecastTransactionDate,
          xScale,
          yScale,
        });
        const negativeArea = createZeroAnchoredArea({
          polarity: "negative",
          valueAccessor: seriesConfig.valueAccessor,
          xAccessor: (d) => d.forecastTransactionDate,
          xScale,
          yScale,
        });

        areaLayer
          .append("path")
          .datum(seriesConfig.data)
          .attr("class", "d3-chart-area dashboard-area-highlight")
          .attr("fill", `url(#${gradients.negativeGradientId})`)
          .attr("d", negativeArea);

        areaLayer
          .append("path")
          .datum(seriesConfig.data)
          .attr("class", "d3-chart-area dashboard-area-highlight")
          .attr("fill", `url(#${gradients.positiveGradientId})`)
          .attr("d", positiveArea);
      });

    const createLine = (valueAccessor) =>
      d3
        .line()
        .defined((d) => Number.isFinite(valueAccessor(d)))
        .x((d) => xScale(d.forecastTransactionDate))
        .y((d) => yScale(valueAccessor(d)))
        .curve(d3.curveMonotoneX);

    series.forEach((seriesConfig) => {
      lineLayer
        .append("path")
        .datum(seriesConfig.data)
        .attr("class", seriesConfig.className)
        .attr("fill", "none")
        .attr("stroke", seriesConfig.color)
        .attr("stroke-width", seriesConfig.strokeWidth)
        .attr("stroke-dasharray", seriesConfig.strokeDasharray ?? null)
        .attr("d", createLine(seriesConfig.valueAccessor));
    });

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    appendTimeSeriesTooltipOverlay({
      chartSvg,
      innerWidth,
      innerHeight,
      xScale,
      getData: () => formattedData,
      xAccessor: (d) => d.forecastTransactionDate,
      showTooltip,
      hideTooltip,
      htmlAccessor: (d) => {
        const avgPoint = dailyAverages.find(
          (avg) =>
            avg.forecastTransactionDate?.getTime?.() ===
            d.forecastTransactionDate?.getTime?.(),
        );

        return buildTooltipHtml({
          title: formatTooltipDate(d.forecastTransactionDate, "%Y-%m-%d %H:%M"),
          rows: [
            {
              label: "Actual",
              value: `${formatTooltipNumber(d.actual, {
                maximumFractionDigits: 0,
              })} MW`,
              color: dashboardSeriesColors.supportGreen,
            },
            {
              label: "Most Likely",
              value: `${formatTooltipNumber(d.mostLikely, {
                maximumFractionDigits: 0,
              })} MW`,
              color: dashboardSeriesColors.accentBlue,
            },
            {
              label: "Avg/Day",
              value: `${formatTooltipNumber(avgPoint?.avgMostLikely, {
                maximumFractionDigits: 0,
              })} MW`,
              color: dashboardSeriesColors.accentRed,
            },
          ],
        });
      },
    });

    const legend = chartSvg
      .append("g")
      .attr("transform", `translate(${innerWidth / 2}, -10)`);

    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text(title)
      .attr("class", "d3-chart-title");

    legend
      .append("line")
      .attr("x1", innerWidth / 4 - 150)
      .attr("y1", 5)
      .attr("x2", innerWidth / 4 - 120)
      .attr("y2", 5)
      .attr("stroke", dashboardSeriesColors.supportGreen)
      .attr("stroke-width", 2);

    legend
      .append("text")
      .attr("x", innerWidth / 4 - 110)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Actual");

    legend
      .append("line")
      .attr("x1", innerWidth / 4 - 60)
      .attr("y1", 5)
      .attr("x2", innerWidth / 4 - 30)
      .attr("y2", 5)
      .attr("stroke", dashboardSeriesColors.accentBlue)
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "4 4");

    legend
      .append("text")
      .attr("x", innerWidth / 4 - 25)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Most Likely");

    legend
      .append("line")
      .attr("x1", innerWidth / 4 + 60)
      .attr("y1", 5)
      .attr("x2", innerWidth / 4 + 90)
      .attr("y2", 5)
      .attr("stroke", dashboardSeriesColors.accentRed)
      .attr("stroke-width", 2);

    legend
      .append("text")
      .attr("x", innerWidth / 4 + 95)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Avg/Day");
  }, [data, title]);

  useEffect(() => {
    !isLoading && renderChart();

    return () => {
      removeTooltip(tooltipRef);
    };
  }, [renderChart, isLoading]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current;
    if (!svgNode) return;

    const throttledResize = () => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100);
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  if (isLoading ?? true) return <Loading />;
  return <svg ref={svgRef} className="dashboard-d3-chart" width="100%" height="100%"></svg>;
};

export default LineGraph;
